const state = {
  items: [],
  deliveryMethod: 'pickup', // 'pickup' or 'delivery'
  deliveryAddress: null,
  checkoutStatus: null
}

const getters = {
  cartItems: state => state.items,
  cartTotal: state => {
    return state.items.reduce((total, item) => {
      return total + (item.price * item.quantity)
    }, 0)
  },
  cartItemCount: state => {
    return state.items.reduce((count, item) => count + item.quantity, 0)
  },
  deliveryMethod: state => state.deliveryMethod,
  deliveryAddress: state => state.deliveryAddress,
  checkoutStatus: state => state.checkoutStatus
}

const actions = {
  addProductToCart({ commit, state }, { product, quantity = 1 }) {
    try {
      // Check if product is valid
      if (!product || !product.uuid) {
        console.error('Invalid product data:', product)
        return { success: false, error: 'Invalid product data' }
      }
      
      // Check if product is in stock
      if (!product.variants || !product.variants.length) {
        console.error('Product has no variants:', product)
        return { success: false, error: 'Product has no variants' }
      }
      
      const variant = product.variants[0]
      let isInStock = false
      
      // Check inventory status
      if (variant.inventory) {
        isInStock = variant.inventory.tracked === false || 
                   (variant.inventory.tracked === true && 
                    variant.inventory.inStock !== undefined && 
                    variant.inventory.inStock > 0)
      } else if (variant.inStock !== undefined) {
        isInStock = variant.inStock
      } else {
        // Default to in stock if we can't determine
        isInStock = true
      }
      
      if (!isInStock) {
        console.error('Product not in stock:', product)
        return { success: false, error: 'Product not in stock' }
      }
      
      // Get price safely
      const price = variant.price && variant.price.amount 
        ? variant.price.amount / 100 
        : 0
      
      // Find item in cart
      const cartItem = state.items.find(item => item.uuid === product.uuid)
      
      if (!cartItem) {
        // Add new item to cart
        commit('ADD_TO_CART', { 
          uuid: product.uuid, 
          name: product.name || 'Unnamed Product', 
          price: price, 
          quantity,
          imageUrl: product.presentation && product.presentation.imageUrl,
          productData: product // Store full product data for reference
        })
      } else {
        // Update quantity of existing item
        commit('INCREMENT_ITEM_QUANTITY', { uuid: product.uuid, quantity })
      }
      
      return { success: true }
    } catch (error) {
      console.error('Error in addProductToCart:', error)
      return { success: false, error: error.message }
    }
  },
  
  removeCartItem({ commit }, uuid) {
    commit('REMOVE_ITEM', uuid)
  },
  
  updateItemQuantity({ commit }, { uuid, quantity }) {
    commit('SET_ITEM_QUANTITY', { uuid, quantity })
  },
  
  setDeliveryMethod({ commit }, method) {
    commit('SET_DELIVERY_METHOD', method)
  },
  
  setDeliveryAddress({ commit }, address) {
    commit('SET_DELIVERY_ADDRESS', address)
  },
  
  clearCart({ commit }) {
    commit('CLEAR_CART')
  },
  
  setCheckoutStatus({ commit }, status) {
    commit('SET_CHECKOUT_STATUS', status)
  },
  
  async saveOrder({ state, commit, rootState }, { orderId, paymentDetails }) {
    try {
      const { db } = await import('@/services/firebase')
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore')
      
      // Get current user if authenticated
      const auth = rootState.auth
      const userId = auth.user ? auth.user.uid : null
      
      // Create order object
      const order = {
        items: state.items,
        total: getters.cartTotal(state),
        deliveryMethod: state.deliveryMethod,
        deliveryAddress: state.deliveryAddress,
        status: 'pending',
        paymentId: orderId,
        paymentDetails,
        userId,
        createdAt: serverTimestamp()
      }
      
      // Save to Firestore
      await addDoc(collection(db, 'orders'), order)
      
      // Clear cart after successful order
      commit('CLEAR_CART')
      commit('SET_CHECKOUT_STATUS', 'success')
      
      return { success: true }
    } catch (error) {
      console.error('Error saving order:', error)
      commit('SET_CHECKOUT_STATUS', 'failed')
      return { success: false, error }
    }
  }
}

const mutations = {
  ADD_TO_CART(state, item) {
    state.items.push(item)
  },
  
  REMOVE_ITEM(state, uuid) {
    state.items = state.items.filter(item => item.uuid !== uuid)
  },
  
  INCREMENT_ITEM_QUANTITY(state, { uuid, quantity }) {
    const cartItem = state.items.find(item => item.uuid === uuid)
    cartItem.quantity += quantity
  },
  
  SET_ITEM_QUANTITY(state, { uuid, quantity }) {
    const cartItem = state.items.find(item => item.uuid === uuid)
    cartItem.quantity = quantity
  },
  
  SET_DELIVERY_METHOD(state, method) {
    state.deliveryMethod = method
  },
  
  SET_DELIVERY_ADDRESS(state, address) {
    state.deliveryAddress = address
  },
  
  CLEAR_CART(state) {
    state.items = []
    state.deliveryAddress = null
  },
  
  SET_CHECKOUT_STATUS(state, status) {
    state.checkoutStatus = status
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 